import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { isPrintTemplate2Enabled } from "../utils/clinicFeatures";

export type InvoicePrintTemplate = "bill" | "printTemplate_2";

type InvoicePrintTemplateContextValue = {
  template: InvoicePrintTemplate;
  setTemplate: (template: InvoicePrintTemplate) => void;
  options: { value: InvoicePrintTemplate; label: string }[];
  showSelector: boolean;
};

const STORAGE_KEY = "invoicePrintTemplate";

const InvoicePrintTemplateContext =
  createContext<InvoicePrintTemplateContextValue | null>(null);

export function InvoicePrintTemplateProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const printTemplate2Enabled = isPrintTemplate2Enabled();

  const options = useMemo(() => {
    const base: { value: InvoicePrintTemplate; label: string }[] = [
      { value: "bill", label: "Bill" },
    ];
    if (printTemplate2Enabled) {
      base.push({ value: "printTemplate_2", label: "Print Template 2" });
    }
    return base;
  }, [printTemplate2Enabled]);

  const [template, setTemplateState] = useState<InvoicePrintTemplate>(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as InvoicePrintTemplate | null;
    if (saved === "printTemplate_2" && printTemplate2Enabled) {
      return saved;
    }
    return "bill";
  });

  useEffect(() => {
    if (!printTemplate2Enabled && template === "printTemplate_2") {
      setTemplateState("bill");
      localStorage.setItem(STORAGE_KEY, "bill");
    }
  }, [printTemplate2Enabled, template]);

  const setTemplate = useCallback((next: InvoicePrintTemplate) => {
    setTemplateState(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const value = useMemo(
    () => ({
      template,
      setTemplate,
      options,
      showSelector: printTemplate2Enabled,
    }),
    [template, setTemplate, options, printTemplate2Enabled]
  );

  return (
    <InvoicePrintTemplateContext.Provider value={value}>
      {children}
    </InvoicePrintTemplateContext.Provider>
  );
}

export function useInvoicePrintTemplate() {
  const context = useContext(InvoicePrintTemplateContext);
  if (!context) {
    throw new Error(
      "useInvoicePrintTemplate must be used within InvoicePrintTemplateProvider"
    );
  }
  return context;
}
